<script lang="ts">
  import type { CinemaSponsoredProduct } from "../../types/CinemaProductsDto.types.ts";
  import ProductImage from "./product-image/ProductImage.svelte";
  import Brand from "./brand/Brand.svelte";
  import Title from "./title/Title.svelte";
  import PriceSection from "./price/PriceSection.svelte";
  import DiscountTag from "./sales-tags/DiscountTag.svelte";
  import BenefitTag from "./sales-tags/BenefitTag.svelte";
  import ProductBadge from "./product-badge/ProductBadge.svelte";
  import SustainabilityBadges from "./sustainability-badges/SustainabilityBadges.svelte";
  import OttoUp from "./otto-up/OttoUp.svelte";
  import EekLabel from "./energy-label/EekLabel.svelte";
  import type { TrackingLabels } from "../../lib/tracking/trackingUtils.ts";
  import {
    buildCinemaFeatureForClick,
    buildClickBaseLabels,
    buildTileFeatureForClick,
  } from "../../lib/tracking/trackingUtils.ts";
  import { onMount, getContext } from "svelte";
  import cinemaTilePromiseKey, {
    type AddPromise,
  } from "../../lib/tracking/cinemaTilePromiseKey";

  type CinemaProductProps = {
    variation: CinemaSponsoredProduct;
    lazyLoad: boolean;
    auctionId: string;
    position: number;
    cinemaTrackingLabels: TrackingLabels;
  };

  const addPromise = getContext<AddPromise | undefined>(cinemaTilePromiseKey);
  if (typeof addPromise === "function") {
    addPromise(
      new Promise<void>((resolve) => {
        onMount(() => resolve());
      }),
    );
  }

  const {
    variation,
    lazyLoad,
    auctionId,
    position,
    cinemaTrackingLabels,
  }: CinemaProductProps = $props();

  const clickFeatures = $derived([
    buildCinemaFeatureForClick(auctionId, cinemaTrackingLabels),
    buildTileFeatureForClick(
      auctionId,
      variation.variationId,
      position,
      variation.tileTrackingLabels as TrackingLabels,
    ),
  ]);

  const clickBaseLabels = $derived(buildClickBaseLabels(clickFeatures));
</script>

<div>
  <div class="tile-content">
    <oc-link-v2
      base64-href={btoa(variation.detailPageLink)}
      oc-aria-label={`${variation.brand} – ${variation.title}`}
    >
      <a
        class="detail-page-url"
        href={variation.canonicalLink}
        data-tr-v1={JSON.stringify(clickBaseLabels)}
        data-tr-v1.track="click,auxclick"
        data-tr-v1.method="submitMove"
        data-tr-v1.action={JSON.stringify({
          name: "click",
          features: clickFeatures,
        })}
      >
        <div class="image-container">
          <ProductImage image={variation.image} {lazyLoad}>
            <ProductBadge variationId={variation.variationId} />
          </ProductImage>
        </div>
        <div>
          <Brand>{variation.brand}</Brand>
          <Title>{variation.title}</Title>
          <PriceSection price={variation.price}></PriceSection>
        </div>
      </a>
    </oc-link-v2>
    <div class="tags">
      {#if variation.salesTags}
        <DiscountTag discount={variation.salesTags.discount} />
      {/if}
      <BenefitTag variationId={variation.variationId} />
    </div>
    {#if variation.ottoUp}
      <OttoUp />
    {/if}
    <div>
      {#if variation.energyLabels.length}
        <EekLabel labels={variation.energyLabels} />
      {/if}
    </div>
    <div>
      <SustainabilityBadges badges={variation.sustainabilityBadges} />
    </div>
  </div>
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .tile-content {
    background-color: tokens.$oc-base-color-gray-25;
    overflow: hidden;
  }

  .image-container {
    overflow: hidden;
    border-radius: tokens.$oc-semantic-border-radius-200;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: tokens.$oc-semantic-spacing-25;
    margin-top: tokens.$oc-semantic-spacing-25;
  }

  .detail-page-url {
    color: tokens.$oc-base-color-black-100;
  }

  .detail-page-url:hover {
    text-decoration: tokens.$oc-base-color-black-100 underline;
    /*                                                                                                                   */
    /*                                                                                                   */
    --tile-hover-decoration-none: #{tokens.$oc-base-color-gray-25} underline;
    --tile-hover-decoration-grey: #{tokens.$oc-semantic-color-text-secondary}
      underline;
  }
</style>
